import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "@components/ui/Container";
import { Section } from "@components/ui/Section";
import { Button } from "@components/ui/Button";
import { faqs, faqCategories } from "@data/faqs";
import { company } from "@data/company";
import type { Faq as FaqType, FaqCategory } from "@app-types/index";
import { Link } from "react-router-dom";
import styles from "./Faq.module.css";

const Chevron = () => (
  <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5l4 4 4-4" />
  </svg>
);

const Search = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const Inbox = () => (
  <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

type FaqProps = {
  /** Si se pasa, muestra una versión simple (sin búsqueda ni categorías) — para el home */
  limit?: number;
};

export function Faq({ limit }: FaqProps) {
  // Modo "home": versión simple con N preguntas, sin filtros
  if (limit) {
    return (
      <Section
        id="faq"
        background="alt"
        align="center"
        eyebrow="Preguntas frecuentes"
        title="Resolvé tus dudas en un click"
        description="Lo que más nos consultan clientes y productores."
      >
        <Container size="narrow">
          <SimpleList items={faqs.slice(0, limit)} />
        </Container>
      </Section>
    );
  }

  // Modo "página completa": full experience
  return (
    <Section background="alt" tight>
      <Container>
        <FullList />
      </Container>
    </Section>
  );
}

function SimpleList({ items }: { items: FaqType[] }) {
  return (
    <>
      <div className={styles.list}>
        {items.map((faq) => (
          <details className={styles.item} key={faq.slug}>
            <summary className={styles.summary}>
              {faq.question}
              <span className={styles.toggle}>
                <Chevron />
              </span>
            </summary>
            <div className={styles.body}>{faq.answer}</div>
          </details>
        ))}
      </div>

      <div className={styles.simpleHelp}>
        <div className={styles.simpleHelpCopy}>
          <strong>¿No encontraste tu duda?</strong>
          <span>Escribinos y un productor te responde sin compromiso.</span>
        </div>
        <Button
          as="a"
          href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hola, tengo una consulta")}`}
          target="_blank"
          rel="noopener"
          variant="cta"
        >
          Escribinos por WhatsApp
        </Button>
      </div>
    </>
  );
}

function FullList() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<FaqCategory | "Todas">("Todas");
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const itemRefs = useRef<Record<string, HTMLDetailsElement | null>>({});

  // Filtrado
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs.filter((faq) => {
      if (category !== "Todas" && faq.category !== category) return false;
      if (!q) return true;
      return (
        faq.question.toLowerCase().includes(q) ||
        faq.answer.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  // Agrupar por categoría para mostrar (solo cuando no hay búsqueda activa)
  const grouped = useMemo(() => {
    if (query.trim()) return null;
    return faqCategories.reduce<Record<string, FaqType[]>>((acc, cat) => {
      const items = filtered.filter((f) => f.category === cat);
      if (items.length) acc[cat] = items;
      return acc;
    }, {});
  }, [filtered, query]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { Todas: faqs.length };
    faqCategories.forEach((cat) => {
      counts[cat] = faqs.filter((f) => f.category === cat).length;
    });
    return counts;
  }, []);

  // Deep-link: abrir el faq cuyo slug esté en el hash al cargar
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const item = itemRefs.current[hash];
    if (item) {
      item.open = true;
      setTimeout(() => item.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, []);

  async function copyLink(slug: string) {
    const url = `${window.location.origin}/faq#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1800);
    } catch {
      // fallback silencioso
    }
  }

  return (
    <div className={styles.wrap}>
      {/* Sidebar categorías desktop */}
      <aside className={styles.sidebar} aria-label="Categorías">
        <div className={styles.sidebarTitle}>Categorías</div>
        <button
          className={`${styles.sidebarLink} ${category === "Todas" ? styles.sidebarLinkActive : ""}`}
          onClick={() => setCategory("Todas")}
        >
          Todas
          <span className={styles.sidebarCount}>{categoryCounts.Todas}</span>
        </button>
        {faqCategories.map((cat) => (
          <button
            key={cat}
            className={`${styles.sidebarLink} ${category === cat ? styles.sidebarLinkActive : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
            <span className={styles.sidebarCount}>{categoryCounts[cat]}</span>
          </button>
        ))}
      </aside>

      <div>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.search} role="search">
            <span className={styles.searchIcon}>
              <Search />
            </span>
            <input
              type="search"
              placeholder="Buscar tu pregunta... (ej. 'siniestro', 'pago')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar en preguntas frecuentes"
            />
            {query && (
              <button
                className={styles.searchClear}
                onClick={() => setQuery("")}
                aria-label="Borrar búsqueda"
                type="button"
              >
                ✕
              </button>
            )}
          </div>

          {/* Chips mobile/tablet */}
          <div className={styles.chips} role="tablist">
            <button
              className={`${styles.chip} ${category === "Todas" ? styles.chipActive : ""}`}
              onClick={() => setCategory("Todas")}
              role="tab"
              aria-selected={category === "Todas"}
            >
              Todas
            </button>
            {faqCategories.map((cat) => (
              <button
                key={cat}
                className={`${styles.chip} ${category === cat ? styles.chipActive : ""}`}
                onClick={() => setCategory(cat)}
                role="tab"
                aria-selected={category === cat}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        {filtered.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <Inbox />
            </div>
            <div className={styles.emptyTitle}>No encontramos resultados</div>
            <p className={styles.emptyBody}>
              Probá con otras palabras o contactanos directamente —
              te respondemos en menos de 24 hs.
            </p>
            <Button
              as="a"
              href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hola, tengo una consulta")}`}
              target="_blank"
              rel="noopener"
              variant="cta"
            >
              Preguntar por WhatsApp
            </Button>
          </div>
        ) : grouped ? (
          // Sin búsqueda → agrupar visualmente por categoría
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h3 className={styles.categoryHeader} id={`cat-${cat}`}>
                {cat}
              </h3>
              <div className={styles.list}>
                {items.map((faq) => (
                  <FaqItem
                    key={faq.slug}
                    faq={faq}
                    refCb={(el) => (itemRefs.current[faq.slug] = el)}
                    onCopy={copyLink}
                    copied={copiedSlug === faq.slug}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // Con búsqueda → lista plana sin agrupación
          <div className={styles.list}>
            {filtered.map((faq) => (
              <FaqItem
                key={faq.slug}
                faq={faq}
                refCb={(el) => (itemRefs.current[faq.slug] = el)}
                onCopy={copyLink}
                copied={copiedSlug === faq.slug}
              />
            ))}
          </div>
        )}

        {/* CTA "no encontré mi duda" */}
        <div className={styles.help}>
          <div className={styles.helpCopy}>
            <h3>¿No encontraste tu duda?</h3>
            <p>Escribinos y un productor te responde sin compromiso.</p>
          </div>
          <div className={styles.helpActions}>
            <Button
              as="a"
              href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hola, tengo una consulta")}`}
              target="_blank"
              rel="noopener"
              variant="cta"
            >
              WhatsApp
            </Button>
            <Link to="/contacto">
              <Button variant="secondary">Formulario</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({
  faq,
  refCb,
  onCopy,
  copied,
}: {
  faq: FaqType;
  refCb: (el: HTMLDetailsElement | null) => void;
  onCopy: (slug: string) => void;
  copied: boolean;
}) {
  return (
    <details className={styles.item} id={faq.slug} ref={refCb}>
      <summary className={styles.summary}>
        {faq.question}
        <span className={styles.toggle}>
          <Chevron />
        </span>
      </summary>
      <div className={styles.body}>
        <p>{faq.answer}</p>
        <div className={styles.bodyFooter}>
          {faq.related ? (
            <Link to={faq.related.to} className={styles.related}>
              {faq.related.label} →
            </Link>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => onCopy(faq.slug)}
            className={`${styles.copyLink} ${copied ? styles.copyLinkOk : ""}`}
            aria-label="Copiar link a esta pregunta"
          >
            {copied ? "✓ Link copiado" : "⎘ Copiar link"}
          </button>
        </div>
      </div>
    </details>
  );
}
